import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { CalendarDays, ExternalLink, FileText, Lightbulb, Search, X } from "lucide-react";
import { buildSearch, nextFixtures, runSearch, wantsNextFixture } from "./searchEngine";
import { fixtureCards } from "./FixturesPage";

const EXAMPLES = [
  "Weigh-in procedures",
  "Current winners",
  "Club rules",
  "When is the next competition?",
  "How much is membership?",
  "Conger competition rules",
];

// Loaded on first open and shared by every later open
let searchPromise = null;
function loadSearch() {
  if (!searchPromise) {
    searchPromise = fetch("/search-index.json")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(({ docs }) => buildSearch(docs))
      .catch((err) => {
        searchPromise = null; // let the next open retry
        throw err;
      });
  }
  return searchPromise;
}

// The "Got a question?" hint shows once per visitor, then never again
const HINT_KEY = "searchHintSeen";
const HINT_DELAY_MS = 4000;
const HINT_DURATION_MS = 10000;

function claimHint() {
  try {
    if (localStorage.getItem(HINT_KEY)) return false;
    localStorage.setItem(HINT_KEY, "1");
    return true;
  } catch {
    return false; // storage blocked: skip the hint rather than show it every visit
  }
}

const isFile = (url) => url.startsWith("/assets/");

// Site pages navigate in-app; PDFs open in a new tab
function ResultLink({ url, onNavigate, className, children }) {
  if (isFile(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={url} onClick={onNavigate} className={className}>
      {children}
    </Link>
  );
}

function Snippet({ snippet }) {
  return (
    <p className="mt-1 text-sm leading-6 text-slate-400">
      {snippet.before && "… "}
      {snippet.parts.map((p, i) => (
        <span key={i}>
          {p.match ? <mark className="rounded bg-cyan-400/20 px-0.5 text-cyan-100">{p.text}</mark> : p.text}{" "}
        </span>
      ))}
      {snippet.after && "…"}
    </p>
  );
}

function FixtureCard({ fixture, label, onNavigate }) {
  return (
    <div className="rounded-2xl border border-amber-300/30 bg-amber-400/10 p-4">
      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-300">
        <CalendarDays className="h-4 w-4" /> {label}
      </p>
      <p className="mt-2 text-base font-semibold text-white">{fixture.type}</p>
      <p className="text-sm text-amber-100">{fixture.date}</p>
      {fixture.venue && <p className="mt-1 text-sm text-slate-300">{fixture.venue}</p>}
      {fixture.time && <p className="text-sm text-slate-300">{fixture.time}</p>}
      <div className="mt-3 flex flex-wrap gap-2">
        <ResultLink url="/fixtures" onNavigate={onNavigate} className="rounded-lg bg-amber-400 px-3 py-1.5 text-sm font-bold text-slate-950 transition hover:bg-amber-300">
          All fixtures
        </ResultLink>
        {fixture.rules && (
          <ResultLink url={fixture.rules} onNavigate={onNavigate} className="inline-flex max-w-full items-center gap-1 rounded-lg border border-amber-300/40 px-3 py-1.5 text-sm font-semibold text-amber-100 transition hover:bg-amber-400/10">
            Rules <ExternalLink className="h-3.5 w-3.5" />
          </ResultLink>
        )}
      </div>
    </div>
  );
}

export default function SiteSearch() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [mini, setMini] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const inputRef = useRef(null);
  const { pathname } = useLocation();
  const [showHint, setShowHint] = useState(false);

  useEffect(() => {
    let hideTimer;
    const showTimer = setTimeout(() => {
      if (!claimHint()) return;
      setShowHint(true);
      hideTimer = setTimeout(() => setShowHint(false), HINT_DURATION_MS);
    }, HINT_DELAY_MS);
    return () => { clearTimeout(showTimer); clearTimeout(hideTimer); };
  }, []);

  useEffect(() => {
    if (open) setShowHint(false);
  }, [open]);

  // "/" or Ctrl/Cmd+K opens search from anywhere (unless already typing somewhere)
  useEffect(() => {
    const onKey = (e) => {
      const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName) || e.target.isContentEditable;
      if ((e.key === "/" && !typing) || (e.key === "k" && (e.metaKey || e.ctrlKey))) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    setLoadError(false);
    loadSearch().then(setMini, () => setLoadError(true));
    const { scrollX, scrollY } = window;
    inputRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      // Put the page back where it was; phones can shift it while the keyboard
      // is open and don't always undo that when it closes.
      document.activeElement?.blur?.();
      document.body.style.overflow = prevOverflow;
      const restore = () => window.scrollTo(scrollX, scrollY);
      restore();
      setTimeout(restore, 350); // again once the keyboard has finished closing
    };
  }, [open]);

  // Phones: keep the window inside the part of the screen the visitor can actually
  // see. iOS doesn't shrink the page for the on-screen keyboard, and lets people
  // pinch-zoom despite maximum-scale=1; without this the window runs under the
  // keyboard or off the sides of a zoomed-in page.
  const [viewport, setViewport] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!open || !vv) return;
    const update = () =>
      setViewport({ top: vv.offsetTop, left: vv.offsetLeft, width: vv.width, height: vv.height });
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, [open]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 150);
    return () => clearTimeout(t);
  }, [query]);

  const result = useMemo(
    () => (mini && debounced.trim() ? runSearch(mini, debounced) : null),
    [mini, debounced]
  );
  const fixtures = useMemo(
    () => (wantsNextFixture(debounced) ? nextFixtures(fixtureCards) : null),
    [debounced]
  );

  const close = () => setOpen(false);
  const hasAnything = result && (result.faq.length || result.results.length || fixtures?.next);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search the site"
        className="inline-flex h-9 items-center gap-2 rounded-xl border border-cyan-400/30 bg-slate-800 px-3 text-sm font-semibold text-slate-200 shadow-[0_14px_28px_-6px_rgba(0,0,0,0.8),0_6px_12px_-4px_rgba(0,0,0,0.6)] transition hover:border-cyan-300/60 hover:bg-slate-700"
      >
        <Search className="h-4 w-4 text-cyan-300" />
        <span className="hidden sm:inline">Ask a question</span>
        <span className="sm:hidden">Ask</span>
      </button>

      {/* Floating copy of the button so search is a thumb's reach away while scrolling */}
      {!open && pathname !== "/admin" && createPortal(
        <>
        {showHint && (
          <div
            role="status"
            className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-[90] w-64 animate-[pop-in_0.3s_ease-out] motion-reduce:animate-none sm:bottom-24 sm:right-6"
          >
            <div className="relative rounded-2xl border border-cyan-300/40 bg-slate-900 p-4 pr-9 shadow-2xl shadow-black/60">
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="text-left"
              >
                <span className="block font-semibold text-white">Got a question?</span>
                <span className="mt-1 block text-sm leading-5 text-slate-300">
                  Ask about rules, fixtures, weigh-ins, records and more.
                </span>
              </button>
              <button
                type="button"
                onClick={() => setShowHint(false)}
                aria-label="Dismiss search tip"
                className="absolute right-2 top-2 rounded-lg p-1 text-slate-400 transition hover:bg-white/10 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
              {/* Pointer towards the floating button */}
              <span className="absolute -bottom-1.5 right-6 h-3 w-3 rotate-45 border-b border-r border-cyan-300/40 bg-slate-900" />
            </div>
          </div>
        )}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Ask a question"
          className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] right-4 z-[90] inline-flex h-14 w-14 items-center justify-center gap-2 rounded-full bg-cyan-400 font-bold text-slate-950 shadow-[0_12px_28px_-4px_rgba(0,0,0,0.85),0_0_0_3px_rgba(34,211,238,0.25)] ring-1 ring-cyan-200/60 transition hover:scale-105 hover:bg-cyan-300 sm:bottom-6 sm:right-6 sm:h-12 sm:w-auto sm:px-5"
        >
          <Search className="h-6 w-6 sm:h-5 sm:w-5" />
          <span className="hidden sm:inline">Ask a question</span>
        </button>
        </>,
        document.body
      )}

      {open && createPortal(
        <div
          className="fixed left-0 top-0 z-[100] flex h-full w-full items-start justify-center bg-slate-950/80 p-3 backdrop-blur-sm sm:px-4 sm:pb-0 sm:pt-[8vh]"
          style={viewport || undefined}
          onClick={close}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Search the site"
            onClick={(e) => e.stopPropagation()}
            className="flex max-h-full w-full min-w-0 max-w-2xl flex-col overflow-hidden [overflow-wrap:anywhere] rounded-2xl border border-white/10 bg-slate-900 shadow-2xl shadow-black/60 animate-[pop-in_0.2s_ease-out] motion-reduce:animate-none sm:max-h-[84vh]"
          >
            <div className="h-0.5 w-full bg-gradient-to-r from-cyan-400 via-cyan-300 to-transparent" />
            <form
              onSubmit={(e) => { e.preventDefault(); setDebounced(query); }}
              className="flex items-center gap-3 border-b border-white/10 px-4 py-3"
            >
              <Search className="h-5 w-5 shrink-0 text-cyan-300" />
              <input
                ref={inputRef}
                type="text"
                enterKeyHint="search"
                autoComplete="off"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask about rules, fixtures, weigh-ins, records…"
                aria-label="Your question"
                className="min-w-0 flex-1 bg-transparent text-base text-white placeholder:text-slate-500 focus:outline-none"
              />
              <button type="button" onClick={close} aria-label="Close search" className="rounded-lg p-1.5 text-slate-400 transition hover:bg-white/10 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </form>

            <div className="min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-4 py-4">
              {loadError && (
                <p className="text-sm text-rose-300">Search couldn't load. Check your connection and try again.</p>
              )}

              {!debounced.trim() && !loadError && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Try asking</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {EXAMPLES.map((ex) => (
                      <button
                        key={ex}
                        type="button"
                        onClick={() => { setQuery(ex); setDebounced(ex); }}
                        className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-slate-300 transition hover:border-cyan-300/40 hover:bg-cyan-400/10 hover:text-white"
                      >
                        {ex}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {debounced.trim() && !mini && !loadError && <p className="text-sm text-slate-400">Loading…</p>}

              {result && (
                <div className="space-y-4">
                  {fixtures?.running.map((f) => (
                    <FixtureCard key={f.type} fixture={f} label="Running now" onNavigate={close} />
                  ))}
                  {fixtures?.next && <FixtureCard fixture={fixtures.next} label="Next fixture" onNavigate={close} />}

                  {result.faq.map((f) => (
                    <div key={f.question} className="rounded-2xl border border-cyan-300/30 bg-cyan-400/10 p-4">
                      <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-cyan-300">
                        <Lightbulb className="h-4 w-4" /> {f.question}
                      </p>
                      <p className="mt-2 text-sm leading-6 text-slate-200">{f.answer}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {f.links.map((l) => (
                          <ResultLink
                            key={l.url}
                            url={l.url}
                            onNavigate={close}
                            className="inline-flex max-w-full items-center gap-1 rounded-lg border border-cyan-300/40 bg-slate-900/60 px-3 py-1.5 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-400/20"
                          >
                            {l.label}
                            {isFile(l.url) && <ExternalLink className="h-3.5 w-3.5" />}
                          </ResultLink>
                        ))}
                      </div>
                    </div>
                  ))}

                  {result.results.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">
                        {result.faq.length || fixtures?.next ? "Also found on the site" : "Found on the site"}
                      </p>
                      <ul className="mt-2 divide-y divide-white/5">
                        {result.results.map((r) => (
                          <li key={r.id}>
                            <ResultLink url={r.url} onNavigate={close} className="-mx-2 block rounded-xl px-2 py-3 transition hover:bg-white/5">
                              <span className="flex items-center gap-2 font-semibold text-white">
                                {r.kind === "pdf" && <FileText className="h-4 w-4 shrink-0 text-amber-300" />}
                                {r.title}
                                {r.page && <span className="text-xs font-normal text-slate-500">page {r.page}</span>}
                              </span>
                              <Snippet snippet={r.snippet} />
                            </ResultLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!hasAnything && (
                    <p className="text-sm text-slate-400">
                      {result.keywords.length
                        ? <>Nothing found for “{result.keywords.join(" ")}”. Try different words, or <Link to="/contact" onClick={close} className="text-cyan-300 underline">ask the club</Link>.</>
                        : "Add a few more words to your question."}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
